import React from "react";
import styled from "styled-components";

import MainHeaderApp from "@appsinti/ui-core/header";

import Current from "./components/Current";
import Hourly from "./components/Hourly";
import Weekly from "./components/Weekly";
import SwitchButton from "../components/SwitchButton";

import { APP_PUBLIC_NAME } from "@appsinti/weather-frontend/constants/config";
import useI18n from "@appsinti/i18n/useI18n";

import { DataProvider, useData } from "./data";
import { Ring } from "@uiball/loaders";
import { BG_DARK, TEXT_PLACEHOLDER } from "@etiquette-ui/colors";

import { getDisplayDate } from "@appsinti/common-utils";

const Body = styled.div`
  position: relative;
  width: 100%;

  .video-preview {
    overflow: hidden;
    max-height: 18rem;
    min-height: 18rem;
    background-color: ${TEXT_PLACEHOLDER};
    width: 100%;
    position: relative;
    display: flex;
    justify-content: center;
  }

  .container {
    width: 100%;
    padding: 0px 15px;
    margin-top: -6rem;

    z-index: 2;
    position: relative;

    .row.mt-4 {
      margin-top: 1.5rem;
    }
  }
`;

const Card = styled.div<{ padding?: string; borderRadius?: string }>`
  max-width: 100%;
  border-radius: ${({ borderRadius }) => (borderRadius ? borderRadius : "8px")};
  margin-bottom: 5px;
  overflow: hidden;
  background-color: white;
  display: flex;
  flex-direction: column;

  .header {
    width: 100%;
    padding: 4px;
    background-color: ${BG_DARK};
  }

  .body {
    padding: ${({ padding }) => (padding ? padding : "15px 24px")};
    width: 100%;
    flex: 1 1 auto;
  }
`;

const CardHeader = styled.div`
  width: 100%;
  padding: 5px 10px;
  font-weight: 600;
  font-size: 1.2rem;
  color: white;
`;

const SwitchContainer = styled.div`
  position: absolute;
  width: 5rem;
  top: 1.5rem;
  right: 2rem;
`;

const VideoUI: React.FC<{ weatherCamPreviewURL: string | null }> = ({
  weatherCamPreviewURL,
}) => {
  return weatherCamPreviewURL ? (
    <img
      src={weatherCamPreviewURL}
      alt="Weather Cam Preview"
      style={{ width: "100%", objectFit: "cover", filter: "brightness(0.7)" }}
    />
  ) : (
    <div style={{ alignSelf: "center" }}>
      <Ring color={"#FFF"} size={22} />
    </div>
  );
};

const Home: React.FC<{ groupId: number | null, groupName?: string }> = ({ groupId, groupName }) => {
  const { translateObject } = useI18n();
  const { loading, current, lastUpdate, weatherCamPreviewURL } = useData();

  return (
    <>
      <MainHeaderApp
        title={translateObject(APP_PUBLIC_NAME)}
        lastUpdated={lastUpdate}
        backButtonLocation={groupId ? `/${groupId}` : ".."}
      />
      <Body>
        <div className="video-preview">
          <VideoUI weatherCamPreviewURL={weatherCamPreviewURL} />
          <SwitchContainer>
            <SwitchButton />
          </SwitchContainer>          
        </div>
        <div className="container">
          <div className="row">
            <Card borderRadius="24px 24px 8px 8px" padding="0">
              <div className="header">
                <CardHeader>
                  {getDisplayDate(lastUpdate, "EEEE, d MMMM yyyy")}{groupName ? ` - ${groupName}` : ""}
                </CardHeader>
              </div>
              <div className="body">
                {loading || !current ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                  </div>
                ) : (
                  <>
                    <Current />
                    <Hourly />
                  </>
                )}
              </div>
            </Card>
          </div>
          <div className="row mt-4">
            <Card>
              <div className="body">
                <Weekly />
              </div>
            </Card>
          </div>
        </div>
      </Body>
    </>
  );
};

const HomeContainer: React.FC<{ appId: number; groupId: number | null; groupName?: string }> = ({
  appId,
  groupId,
  groupName
}) => {
  return (
    <DataProvider appId={appId}>
      <Home groupId={groupId} groupName={groupName} />
    </DataProvider>
  );
};

export default HomeContainer;
